import {
  AutocompleteInput,
  FormGetter,
  FormInput,
  FormPageLayout,
  HasuraAutocompleteRef,
  HasuraSelector,
  HasuraSelectorRef,
  HiddenInput,
  NotificationsContext,
  OpenInNewInputAdorement,
  useNavigate,
} from '@kirz/mui-admin';
import { Add, OpenInBrowser, OpenInNew, Replay } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, IconButton, Tooltip } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { PhoneMessageOutline } from 'mdi-material-ui';
import React, { useContext, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';

import { AudioPlayer } from 'components/AudioPlayer';
import { NewClientDialog } from 'components/NewClientDialog';
import { NewDealDialog } from 'components/NewDealDialog';
import { TagsGroup } from 'components/TagsGroup';
import { UserContext } from 'contexts';
import { SettingsContext } from 'contexts/SettingsContext';
import { api } from 'services/api';

function GptReextractButton({
  callId,
  onComplete,
}: {
  callId: number;
  onComplete: () => void;
}) {
  const { showAlert } = useContext(NotificationsContext);
  const [isReextracting, setIsReextracting] = useState(false);

  return (
    <LoadingButton
      variant="outlined"
      startIcon={<Replay />}
      loading={isReextracting}
      onClick={async () => {
        try {
          setIsReextracting(true);
          await api.instance.post(`/megafon/re-extract-text/${callId}`);
          onComplete();
          showAlert('GPT-обработка завершена', 'success');
        } finally {
          setIsReextracting(false);
        }
      }}
    >
      GPT-обработка
    </LoadingButton>
  );
}

export function Call() {
  const routeParams = useParams() as Record<string, string>;
  const callId = parseInt(routeParams.id, 10);
  const { tags } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const clientSelectorRef = useRef<HasuraSelectorRef>(null);
  const [isNewClientDialogOpened, setIsNewClientDialogOpened] = useState(false);
  const { showAlert, showPrompt } = useContext(NotificationsContext);
  const dealsAutocompleteRef = useRef<HasuraAutocompleteRef>(null);
  const [formKey, setFormKey] = useState(0);

  return (
    <>
      <FormPageLayout
        key={formKey}
        source="call"
        breadcrumbs={[
          { text: 'Звонки', icon: PhoneMessageOutline, href: '/calls' },
          (item) => ({
            text: `Звонок #${item.id} от ${dayjs(item.createdAt).format(
              'DD.MM.YYYY HH:mm',
            )}`,
            copyOnClick: true,
          }),
        ]}
        breadcrumbsDeps={['createdAt']}
        defaultRoute="/calls"
        tagsSlot={
          <Box sx={{ ml: 1 }}>
            <FormGetter
              names={['tags']}
              render={({ tags: callTags }) =>
                callTags && (
                  <TagsGroup ids={callTags} size="small" placeholder={false} />
                )
              }
            />
          </Box>
        }
      >
        <HasuraSelector selection="clientPhone" />
        <HasuraSelector selection="createdAt" />
        <HasuraSelector selection="recordLink" />
        {/* <HasuraSelector selection="dealId" /> */}

        <HasuraSelector
          ref={clientSelectorRef}
          name="clientByPhone"
          selection="clientsByPhone {clientId}"
          resolveValue={({ clientsByPhone }) => {
            return {
              clientByPhone: clientsByPhone?.[0],
            };
          }}
        />
        <Grid container sx={{ m: 0 }}>
          <FormGetter
            names={['recordLink']}
            render={(x) => (
              <Grid xs={12} sx={{ mb: 3, height: '68px', overflow: 'hidden' }}>
                {x.recordLink && (
                  <AudioPlayer callId={callId} url={x.recordLink} />
                )}
                {x.recordLink === null && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    Запись звонка отсутствует
                  </Box>
                )}
              </Grid>
            )}
          />
          <Grid
            xs={12}
            sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}
          >
            <GptReextractButton
              callId={callId}
              onComplete={() => setFormKey((key) => key + 1)}
            />
          </Grid>
          <FormGetter
            names={['clientByPhone']}
            render={(x) =>
              x?.clientByPhone?.clientId ? (
                <AutocompleteInput
                  value={x.clientByPhone.clientId}
                  name="clientId"
                  mode="hasura"
                  source="client"
                  label="Клиент"
                  selection="id fullName"
                  itemText="fullName"
                  md={3}
                  fetchAll={false}
                  required
                  readOnly
                  inputProps={{
                    InputProps: {
                      startAdornment: (
                        <OpenInNewInputAdorement
                          field="clientId"
                          baseUrl="/clients"
                          entityId={x.clientByPhone.clientId}
                        />
                      ),
                    },
                  }}
                />
              ) : (
                <FormGetter
                  names={['clientByPhone', 'clientPhone']}
                  render={(x) =>
                    !x?.clientByPhone?.clientId && (
                      <Grid
                        md={3}
                        sx={{ alignItems: 'center', display: 'flex' }}
                      >
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setIsNewClientDialogOpened(true)}
                          >
                            Создать клиента
                          </Button>
                          <Tooltip title="Создать в новой вкладке">
                            <IconButton
                              href={`/clients/new?phone=${x.clientPhone}`}
                            >
                              <OpenInNew />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Grid>
                    )
                  }
                />
              )
            }
          />
          <FormGetter
            names={['clientByPhone']}
            onChange={(newValue, prevValue) => {
              // setValue('dealId', null);
            }}
            render={(x) => {
              const clientId = x?.clientByPhone?.clientId;

              // if (!clientId) {
              //   return null;
              // }

              return (
                <AutocompleteInput
                  controlRef={dealsAutocompleteRef}
                  name="dealId"
                  mode="hasura"
                  source="deal"
                  label="Сделка"
                  selection="id name"
                  itemText={(x) =>
                    `Сделка #${x.id}${x.name ? `: ${x.name}` : ''}`
                  }
                  md={3}
                  fetchAll
                  disabled={!clientId}
                  inputProps={{
                    InputProps: {
                      startAdornment: (
                        <OpenInNewInputAdorement
                          field="dealId"
                          baseUrl="/deals"
                          entityId={x.dealId}
                        />
                      ),
                    },
                  }}
                  filter={{
                    _or: [
                      { clientId: { _eq: clientId } },
                      { sellerId: { _eq: clientId } },
                      { clients: { clientId: { _eq: clientId } } },
                    ],
                  }}
                  disableFetch={clientId === undefined}
                  onFetch={(items) => {
                    return items;
                  }}
                  addNewOption={{
                    text: 'Добавить сделку',
                    async onClick() {
                      const values = await showPrompt({
                        title: 'Новая сделка',
                        // form: (
                        //   <FormInput
                        //     name="address"
                        //     label="Адрес"
                        //     required
                        //     autoFocus
                        //   />
                        // ),
                        dialog: NewDealDialog,
                        accept: 'Создать',
                        width: 500,
                        formSubmitterProps: {
                          source: 'estateAddress',
                          preSubmit: (item) => ({
                            ...item,
                            residentialComplexId: x.residentialComplex.id,
                          }),
                          selection: ['id'],
                        },
                      });

                      await dealsAutocompleteRef.current?.refetch();
                    },
                  }}
                />
              );
            }}
          />

          <AutocompleteInput
            name="userId"
            mode="hasura"
            source="user"
            label="Сотрудник"
            selection="id fullName"
            itemText="fullName"
            md={3}
            readOnly
          />
          <AutocompleteInput
            name="tags"
            label="Теги"
            multiple
            md={3}
            options={tags
              .filter((x) => !x.systemType && x.entity === 'call')
              .map((x) => ({ value: x.id, text: x.name }))}
            formFetcherValueResolver={{
              source: 'callTag',
              selection: 'tagId',
              filter: { callId: { _eq: callId } },
              resolveItems: (items) => items.map((x) => x.tagId),
            }}
            formSubmitterValueResolver={{
              type: 'hasura-many-to-many',
              source: 'callTag',
              foreignKey: 'tagId',
              entityId: { callId, companyId: user.companyId },
            }}
          />
          <FormInput name="comment" label="Комментарий" multiline rows={4} />
          <FormInput
            name="extractedText"
            label="Распознанный текст"
            multiline
            // rows={20}
            readOnly
          />

          <FormGetter
            names={['extractedTextMarkdown']}
            render={({ extractedTextMarkdown }) => (
              <Grid xs={12} sx={{ mb: 3 }}>
                <HiddenInput name="extractedTextMarkdown" />
                {extractedTextMarkdown && (
                  <Box>
                    <Box
                      sx={{
                        mb: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                      }}
                    >
                      Краткий пересказ разговора
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#fafafa',
                        // maxHeight: 400,
                        // overflowY: 'auto',
                      }}
                    >
                      <ReactMarkdown
                      // components={{
                      //   body: ({ node, ...props }) => (
                      //     <Box {...(props as any)} sx={{ width: '100%' }} />
                      //   ),
                      // }}
                      >
                        {extractedTextMarkdown}
                      </ReactMarkdown>
                    </Box>
                  </Box>
                )}
              </Grid>
            )}
          />

          <FormGetter
            names={['callSummarizationMarkdown']}
            render={({ callSummarizationMarkdown }) => (
              <Grid xs={12} sx={{ mb: 3 }}>
                <HiddenInput name="callSummarizationMarkdown" />
                {callSummarizationMarkdown && (
                  <Box>
                    <Box
                      sx={{
                        mb: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                      }}
                    >
                      Сильные и слабые стороны разговора
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#fafafa',
                        // maxHeight: 300,
                        // overflowY: 'auto',
                      }}
                    >
                      <ReactMarkdown>{callSummarizationMarkdown}</ReactMarkdown>
                    </Box>
                  </Box>
                )}
              </Grid>
            )}
          />

          <FormGetter
            names={['callAnalyzeMarkdown']}
            render={({ callAnalyzeMarkdown }) => (
              <Grid xs={12} sx={{ mb: 3 }}>
                <HiddenInput name="callAnalyzeMarkdown" />
                {callAnalyzeMarkdown && (
                  <Box>
                    <Box
                      sx={{
                        mb: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                      }}
                    >
                      Рекомендации для продавца
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#fafafa',
                        // maxHeight: 350,
                        // overflowY: 'auto',
                      }}
                    >
                      <ReactMarkdown>{callAnalyzeMarkdown}</ReactMarkdown>
                    </Box>
                  </Box>
                )}
              </Grid>
            )}
          />

          <FormGetter
            names={['clientPhone']}
            render={(x) => (
              <NewClientDialog
                open={isNewClientDialogOpened}
                onClose={() => setIsNewClientDialogOpened(false)}
                formProps={{
                  defaultValues: {
                    phone: x.clientPhone,
                  },
                }}
                onSubmit={(item) => {
                  clientSelectorRef.current?.refetch();
                }}
              />
            )}
          />
        </Grid>
      </FormPageLayout>
    </>
  );
}
